import { MutationTree } from "vuex";
import { MetadataState, Metadata, MetadataType, MetadataObjectType } from "./types";
import { RootState } from "../types";
import Vue from "vue";
import { getDefaultState } from "./state";

export const mutations: MutationTree<MetadataState> = {
	setMetadataTypes(state, types: MetadataType[]) {
		state.metadataTypes = types;
	},

	setObjectTypes(state, types: MetadataObjectType[]) {
		state.metadataObjectTypes = types;
	},

	/**
	 * Adds/updates a set of metadata within the Metadata Store.
	 */
	setMetadata(state, { metadata, pruneEntries }) {
		// Add/update any new or existing items of metadata
		for (var item of metadata as Metadata[]) {
			// Parse the data property
			item.data = JSON.parse(item.data);

			// Build an index string
			var index = JSON.stringify({
				objectId: item.objectId,
				objectTypeId: item.objectType.objectTypeId,
				metadataTypeId: item.metadataType.metadataTypeId
			});

			// Do we already have an entry in the map for this type of object/metadata?
			let currentMetadata = state.metadata[index];

			if (currentMetadata) {
				/**
				 * Get the current index of this metadata item (by id) in the map entry.
				 * If this item is in the map (index > -1), we overwrite the existing value.
				 * Otherwise, we push it to the array.
				 **/
				let currentIndex = currentMetadata.findIndex(m => m.metadataId == item.metadataId);

				if (currentIndex > -1) {
					Vue.set(currentMetadata, currentIndex, item);
				} else {
					currentMetadata.push(item);
				}
			} else {
				// No entry in the map, so create a new array and add the metadata item.
				currentMetadata = [item];
			}

			Vue.set(state.metadata, index, currentMetadata);
		}

		// pruneEntries will be true if setMetadata is being called from the loadMetadata action. If it's being
		// called by createMetadata, it will be false.
		if (pruneEntries) {
			// Find all metadata items that are in the store, but not in the response data (these have been deleted)
			const itemsToDelete = Object.values(state.metadata)
				.flatMap(m => m)
				.filter(
					itemInStore => !metadata.find(itemInResponse => itemInResponse.metadataId == itemInStore.metadataId)
				);

			// Go through each item and delete it from the store
			itemsToDelete.forEach(({ metadataId, objectId, objectType, metadataType }) => {
				let mapIndex = JSON.stringify({
					objectId,
					objectTypeId: objectType.objectTypeId,
					metadataTypeId: metadataType.metadataTypeId
				});

				let metadataIndex = state.metadata[mapIndex].findIndex(m => m.metadataId == metadataId);
				Vue.delete(state.metadata[mapIndex], metadataIndex);
			});
		}
	},

	/**
	 * Removes an item of Metadata (by id, object id, and metadata type id) from the store.
	 */
	deleteMetadata(state, { metadataId, objectId, objectTypeId, metadataTypeId }) {
		// Build an index string
		let mapIndex = JSON.stringify({
			objectId,
			objectTypeId,
			metadataTypeId
		});

		let metadataIndex = state.metadata[mapIndex]?.findIndex(m => m.metadataId == metadataId) ?? -1;
		if (metadataIndex < 0) {
			return;
		}

		// Delete the metadata item from our store
		Vue.delete(state.metadata[mapIndex], metadataIndex);
	},

	/**
	 * Resets the state of the metadata store.
	 */
	resetState(state) {
		Object.assign(state, getDefaultState());
	}
};
